<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  FEATURE_FLAGS,
  CAPTAIN_ADDITIONAL_FEATURE_FLAGS,
  CAPTAIN_TOOLS_FEATURE_FLAGS,
  CAPTAIN_CUSTOM_TOOLS_FEATURE_FLAGS,
} from 'dashboard/featureFlags';
import { usePolicy } from 'dashboard/composables/usePolicy';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import CustomToolsPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/CustomToolsPageEmptyState.vue';
import CreateCustomToolDialog from 'dashboard/components-next/captain/pageComponents/customTool/CreateCustomToolDialog.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import AgentConfirmDialog from 'dashboard/routes/dashboard/captain/assistants/experience/components/AgentConfirmDialog.vue';
import AgentContextPicker from './components/AgentContextPicker.vue';
import ToolActionsCatalog from './components/ToolActionsCatalog.vue';
import ToolGovernanceApprovals from './components/ToolGovernanceApprovals.vue';
import CustomToolsCatalog from './components/CustomToolsCatalog.vue';
import { useToolManagement } from './composables/useToolManagement';
import { useCustomToolsCatalog } from './composables/useCustomToolsCatalog';
import { useToolsAssistantContext } from './composables/useToolsAssistantContext';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { uiSettings } = useUISettings();
const { isFeatureFlagEnabled, shouldShowPaywall, checkPermissions } =
  usePolicy();

const canManageAssistant = computed(() => checkPermissions(['administrator']));
const {
  assistants,
  selectedAssistantId: assistantId,
  assistantContextError,
  isFetchingAssistants,
  loadAssistants,
  selectAssistant,
} = useToolsAssistantContext({ store, route, router, uiSettings, t });
const hasAssistantContext = computed(() => Boolean(assistantId.value));
const hasAdvancedManagement = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.CAPTAIN_ADVANCED_MANAGEMENT)
);
const hasAdvancedOperations = computed(() =>
  isFeatureFlagEnabled(CAPTAIN_ADDITIONAL_FEATURE_FLAGS)
);
const hasAdvancedApprovals = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.CAPTAIN_ADVANCED_APPROVALS)
);
const hasCustomToolsEntitlement = computed(() =>
  isFeatureFlagEnabled(CAPTAIN_CUSTOM_TOOLS_FEATURE_FLAGS)
);
const showCustomToolsPaywall = computed(
  () =>
    !hasCustomToolsEntitlement.value &&
    shouldShowPaywall(CAPTAIN_CUSTOM_TOOLS_FEATURE_FLAGS)
);

const {
  isFetchingManagement,
  isSavingGovernance,
  savingActionId,
  savingAvailabilityId,
  approvalActionId,
  actionSearch,
  filteredActions,
  actionTypeLabels,
  pendingApprovals,
  approvalsMeta,
  isLoadingMoreApprovals,
  hasMoreApprovals,
  availabilityActionPending,
  governanceForm,
  linkedScenarioCount,
  linkedCustomScenarioCount,
  isActionApprovalLocked,
  updateGovernance,
  refreshAfterCustomToolChange,
  loadMoreApprovals,
  requestToggleActionAvailability,
  confirmToggleActionAvailability,
  closeActionAvailabilityDialog,
  toggleActionApproval,
  saveGovernance,
  decideApproval,
} = useToolManagement({
  store,
  t,
  assistantId,
  hasAssistantContext,
  canManageAssistant,
  hasAdvancedOperations,
  hasAdvancedApprovals,
});

const {
  customTools,
  customToolsMeta,
  customToolsError,
  isFetchingCustomTools,
  isCustomToolsPageFetching,
  showSoftLimitWarning,
  customToolAvailabilityPending,
  savingCustomToolId,
  createDialogRef,
  deleteDialogRef,
  selectedTool,
  dialogType,
  fetchCustomTools,
  initializeCustomTools,
  onPageChange,
  openCreateDialog,
  handleDialogClose,
  onDeleteSuccess,
  handleCardAction,
  confirmToggleCustomToolAvailability,
  closeCustomToolAvailabilityDialog,
} = useCustomToolsCatalog({
  store,
  t,
  assistantId,
  hasAssistantContext,
  canManageAssistant,
  hasAdvancedOperations,
  hasAdvancedManagement,
  hasCustomToolsEntitlement,
  linkedCustomScenarioCount,
  refreshManagement: refreshAfterCustomToolChange,
});

const isPageFetching = computed(
  () => isCustomToolsPageFetching.value || isFetchingManagement.value
);

const openScenarios = () => {
  if (!assistantId.value) return;
  router.push({
    name: 'captain_assistants_scenarios_index',
    params: {
      accountId: route.params.accountId,
      assistantId: assistantId.value,
    },
  });
};

const initializeToolsPage = () => {
  initializeCustomTools();
  loadAssistants();
};

onMounted(initializeToolsPage);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.CUSTOM_TOOLS.HEADER')"
    :button-label="
      hasCustomToolsEntitlement ? $t('CAPTAIN.CUSTOM_TOOLS.ADD_NEW') : ''
    "
    :button-policy="['administrator']"
    :feature-flag="CAPTAIN_TOOLS_FEATURE_FLAGS"
    :total-count="customToolsMeta.totalCount"
    :current-page="customToolsMeta.page"
    :show-pagination-footer="
      !isFetchingCustomTools && !customToolsError && !!customTools.length
    "
    :is-fetching="isPageFetching"
    :is-empty="false"
    :show-know-more="false"
    :show-assistant-switcher="false"
    @update:current-page="onPageChange"
    @click="openCreateDialog"
  >
    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #emptyState>
      <CustomToolsPageEmptyState @click="openCreateDialog" />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 sm:gap-8">
        <AgentContextPicker
          v-if="hasAdvancedOperations"
          :model-value="assistantId || ''"
          :assistants="assistants"
          :is-loading="isFetchingAssistants"
          :load-error="assistantContextError"
          @update:model-value="selectAssistant"
          @retry="loadAssistants"
        />

        <template v-if="hasAdvancedOperations && hasAssistantContext">
          <ToolActionsCatalog
            v-model:search="actionSearch"
            :actions="filteredActions"
            :action-type-labels="actionTypeLabels"
            :linked-scenario-count="linkedScenarioCount"
            :is-action-approval-locked="isActionApprovalLocked"
            :saving-availability-id="savingAvailabilityId"
            :saving-action-id="savingActionId"
            :can-manage="canManageAssistant"
            :has-advanced-approvals="hasAdvancedApprovals"
            @open-scenarios="openScenarios"
            @toggle-availability="requestToggleActionAvailability"
            @toggle-approval="toggleActionApproval"
          />

          <ToolGovernanceApprovals
            :show-approvals="hasAdvancedApprovals"
            :can-manage="canManageAssistant"
            :approvals="pendingApprovals"
            :active-approval-id="approvalActionId"
            :approvals-meta="approvalsMeta"
            :has-more-approvals="hasMoreApprovals"
            :is-loading-more-approvals="isLoadingMoreApprovals"
            :governance="governanceForm"
            :is-saving-governance="isSavingGovernance"
            @decide="decideApproval"
            @load-more="loadMoreApprovals"
            @update:governance="updateGovernance"
            @save-governance="saveGovernance"
          />
        </template>

        <CustomToolsCatalog
          :show-paywall="showCustomToolsPaywall"
          :has-entitlement="hasCustomToolsEntitlement"
          :load-error="customToolsError"
          :is-fetching="isFetchingCustomTools"
          :show-soft-limit-warning="showSoftLimitWarning"
          :tools="customTools"
          :can-manage="canManageAssistant"
          :saving-tool-id="savingCustomToolId"
          :current-page="customToolsMeta.page"
          @retry="fetchCustomTools"
          @create="openCreateDialog"
          @action="handleCardAction"
        />
      </div>
    </template>
  </PageLayout>

  <CreateCustomToolDialog
    v-if="dialogType"
    ref="createDialogRef"
    :type="dialogType"
    :selected-tool="selectedTool"
    @close="handleDialogClose"
  />

  <DeleteDialog
    v-if="selectedTool"
    ref="deleteDialogRef"
    :entity="selectedTool"
    type="CustomTools"
    translation-key="CUSTOM_TOOLS"
    :description="
      $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DELETE_CUSTOM_DESCRIPTION', {
        count: linkedCustomScenarioCount(selectedTool),
      })
    "
    @delete-success="onDeleteSuccess"
  />

  <AgentConfirmDialog
    :show="Boolean(availabilityActionPending)"
    :title="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_ACTION_TITLE')"
    :description="
      $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_ACTION_DESCRIPTION', {
        count: availabilityActionPending
          ? linkedScenarioCount(availabilityActionPending)
          : 0,
      })
    "
    :confirm-text="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_ACTION_CONFIRM')"
    @close="closeActionAvailabilityDialog"
    @confirm="confirmToggleActionAvailability"
  />
  <AgentConfirmDialog
    :show="Boolean(customToolAvailabilityPending)"
    :title="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_CUSTOM_TITLE')"
    :description="
      $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_CUSTOM_DESCRIPTION', {
        count: customToolAvailabilityPending
          ? linkedCustomScenarioCount(customToolAvailabilityPending)
          : 0,
      })
    "
    :confirm-text="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_CUSTOM')"
    @close="closeCustomToolAvailabilityDialog"
    @confirm="confirmToggleCustomToolAvailability"
  />
</template>
